import * as React from "react";

import { Box } from "@dropbox/dig-foundations";
import type { Atoms } from "@dropbox/dig-foundations";

export interface SplitBoxProps {
  /**
   * The content to display on the left side of the split box.
   */
  leftContent: React.ReactNode;

  /**
   * The content to display on the right side of the split box.
   */
  rightContent: React.ReactNode;

  /**
   * The ratio of the split box. A value between 0 and 1 representing the width proportion
   * of the left content. For example, 0.5 means an equal split, 0.25 means the left content
   * takes up 25% of the width.
   * @default 0.5
   */
  ratio?: number;

  /**
   * The height of the split box.
   * @default "fit-content"
   */
  height?: Atoms["height"];

  /**
   * Add a border between the left and right content.
   * @default false
   */
  border?: boolean;

  /**
   * The color of the border. Only applies if `border` is `true`.
   * @default "Border Base"
   */
  borderColor?: Atoms["borderColor"];

  /**
   * The width of the border. Only applies if `border` is `true`.
   * @default "1"
   */
  borderWidth?: Atoms["borderWidth"];
}

/**
 * A container that splits a box horizontally into two
 * partitions based on a provided ratio. Contents will
 * be rendered on either side of the split. This component
 * will always expand to fill its parent's width.
 */
export const SplitBox: React.FC<SplitBoxProps> = ({
  leftContent,
  rightContent,
  ratio = 0.5,
  height = "fit-content",
  border = false,
  borderColor = "Border Base",
  borderWidth = "1",
}) => {
  return (
    <Box width="100%" height={height} display="flex">
      <Box
        minHeight="100%"
        style={{ width: `${ratio * 100}%` }}
        borderRight={border ? "Solid" : undefined}
        borderColor={borderColor}
        borderWidth={borderWidth}
      >
        {leftContent}
      </Box>
      <Box minHeight="100%" style={{ width: `${(1 - ratio) * 100}%` }}>
        {rightContent}
      </Box>
    </Box>
  );
};
